import { Inbox, CheckCircle2, Star, Calendar, Clock, Moon, Users, Target, Repeat } from 'lucide-react';

const EMPTY_STATE_CONFIGS = {
  inbox: {
    icon: Inbox,
    title: "Inbox Zero!",
    subtitle: "You've processed everything. Great job staying on top of things.",
    tip: "Quick capture: Press 'N' anywhere to add a new item to your inbox.",
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10'
  },
  today: {
    icon: Star,
    title: "Your Day is Open",
    subtitle: "No tasks committed for today. Process your inbox or schedule something.",
    tip: "The Today list is sacred — only add tasks you're committed to completing today.",
    color: 'text-accent',
    bgColor: 'bg-accent/10',
    actions: [
      { label: 'Process Inbox', navigateTo: 'inbox' },
      { label: 'Add Task', action: 'addTask' }
    ]
  },
  scheduled: {
    icon: Calendar,
    title: "Nothing Scheduled",
    subtitle: "No future-dated tasks. Schedule tasks to plan ahead.",
    tip: "Use scheduled tasks for things with hard deadlines or that need to happen on specific days.",
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10'
  },
  waiting: {
    icon: Clock,
    title: "Nothing Pending",
    subtitle: "No tasks waiting on others. You're in control!",
    tip: "Use 'Waiting' for delegated tasks or things blocked on someone else. Set follow-up dates to stay on top of them.",
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10'
  },
  someday: {
    icon: Moon,
    title: "Dream Big",
    subtitle: "Your someday list is empty. Capture ideas you might want to do later.",
    tip: "Someday/Maybe is for ideas without commitment. Review weekly to keep dreams alive.",
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-500/10'
  },
  habits: {
    icon: Repeat,
    title: "Build Your Routines",
    subtitle: "Habits help you maintain consistency. Add your first habit to get started.",
    tip: "Start small: one daily habit is better than five you won't keep.",
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    actions: [
      { label: 'Add First Habit', action: 'addHabit' }
    ]
  },
  oneOnOnes: {
    icon: Users,
    title: "Track Your Team",
    subtitle: "1:1 meetings help build relationships. Add your first relationship to get started.",
    tip: "Never forget what you discussed — capture talking points before and action items after.",
    color: 'text-weekly',
    bgColor: 'bg-weekly/10',
    actions: [
      { label: 'Add Relationship', action: 'addRelationship' }
    ]
  },
  goals: {
    icon: Target,
    title: "Set Your Direction",
    subtitle: "Goals give your tasks purpose. What are you working toward?",
    tip: "Link tasks to goals to see progress and stay motivated.",
    color: 'text-monthly',
    bgColor: 'bg-monthly/10',
    actions: [
      { label: 'Add First Goal', action: 'addGoal' }
    ]
  },
  dashboard: {
    icon: CheckCircle2,
    title: "Ready to Start",
    subtitle: "Your dashboard is clear. Add some tasks to your inbox to get going.",
    tip: "Start with a brain dump — capture everything on your mind without organizing.",
    color: 'text-accent',
    bgColor: 'bg-accent/10',
    actions: [
      { label: 'Brain Dump', action: 'brainDump' },
      { label: 'Quick Add', action: 'addTask' }
    ]
  }
};

export default function EmptyState({
  type,
  onAction,
  onNavigate,
  customTitle,
  customSubtitle,
  customIcon: CustomIcon,
  showTip = true,
  compact = false
}) {
  const config = EMPTY_STATE_CONFIGS[type] || EMPTY_STATE_CONFIGS.inbox;
  const Icon = CustomIcon || config.icon;

  const handleAction = (action) => {
    if (action.navigateTo && onNavigate) {
      onNavigate(action.navigateTo);
    } else if (action.action && onAction) {
      onAction(action.action);
    }
  };

  if (compact) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center mb-3`}>
          <Icon className={`w-6 h-6 ${config.color}`} />
        </div>
        <p className="text-text-secondary text-sm">{customTitle || config.title}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      {/* Icon */}
      <div className={`w-20 h-20 rounded-2xl ${config.bgColor} flex items-center justify-center mb-6`}>
        <Icon className={`w-10 h-10 ${config.color}`} />
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-text-primary mb-2">
        {customTitle || config.title}
      </h3>

      {/* Subtitle */}
      <p className="text-text-secondary mb-6 max-w-sm">
        {customSubtitle || config.subtitle}
      </p>

      {/* Actions */}
      {config.actions && config.actions.length > 0 && (
        <div className="flex gap-3 mb-6">
          {config.actions.map((action, i) => (
            <button
              key={i}
              onClick={() => handleAction(action)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                i === 0
                  ? 'bg-accent text-white hover:bg-orange-hover'
                  : 'bg-surface-base text-text-secondary hover:bg-surface-hover'
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Tip */}
      {showTip && config.tip && (
        <div className="bg-surface-base rounded-xl p-4 max-w-md">
          <p className="text-sm text-text-muted">
            <span className="font-medium text-text-secondary">Tip: </span>
            {config.tip}
          </p>
        </div>
      )}
    </div>
  );
}
